import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Check } from "lucide-react";

const LAST_PICK_KEY = "trubeaute.contour_choice.last";

/**
 * 7 contour vibes, in display order. Each value maps to a Literal in
 * the backend `AnalyzeRequest.contour_choice` field — keep these IDs
 * synchronised with `CONTOUR_VIBE_HINTS` in server.py.
 *
 * Copy is the founder-approved vocabulary from the spec.
 */
const VIBES = [
  {
    value: "soft_lifted",
    label: "Soft & lifted",
    description: "Gentle warmth, diffused edges. A whisper of lift.",
  },
  {
    value: "natural_definition",
    label: "Natural definition",
    description: "Polished but invisible. Like the light just hits right.",
  },
  {
    value: "sculpted_glam",
    label: "Sculpted glam",
    description: "Confident definition. Main character energy.",
  },
  {
    value: "snatched",
    label: "Snatched contour",
    description: "Precise, sharp, and definitely intentional.",
  },
  {
    value: "androgynous",
    label: "Androgynous contour",
    description: "Structural and architectural. Honors your features.",
  },
  {
    value: "dramatic_editorial",
    label: "Dramatic / editorial",
    description: "Graphic, fashion-forward, made for the lens.",
  },
  {
    value: "drag_stage",
    label: "Drag / stage contour",
    description: "Full transformation. Reads from the back of the room.",
  },
];

/**
 * ContourVibePicker — inline two-step prompt on Capture screen.
 *   Step 1: "Would you like to explore contouring today?" → Yes / Not right now
 *   Step 2: (if Yes) 7 vibe chips, single-select, with soft memory.
 *
 * Soft-memory: the last picked vibe is stored in localStorage and
 * pre-selected next session (Capture is a per-routine choice, so we
 * don't persist to the user profile — but it's friendlier than asking
 * cold every time).
 *
 * Props:
 *   - value: current contour_choice in session ("skip" | vibe id | null)
 *   - onChange(next): updates session.preferences.contour_choice
 */
const ContourVibePicker = ({ value, onChange }) => {
  // Whether the user is in "picking a vibe" state. Starts true if a
  // vibe is already selected (so they can change it without re-clicking
  // Yes); false if value is "skip" or null.
  const [showVibes, setShowVibes] = useState(
    value !== null && value !== "skip",
  );

  // Soft-memory hydration: pre-select last-picked vibe when the user
  // clicks Yes if they haven't picked one yet this session.
  const lastPick = useMemo(() => {
    try {
      const raw = localStorage.getItem(LAST_PICK_KEY);
      return raw && VIBES.some((v) => v.value === raw) ? raw : null;
    } catch {
      return null;
    }
  }, []);

  // Persist whenever a real vibe is selected (not skip)
  useEffect(() => {
    if (value && value !== "skip") {
      try {
        localStorage.setItem(LAST_PICK_KEY, value);
      } catch {
        /* localStorage unavailable — soft-memory just won't persist */
      }
    }
  }, [value]);

  const handleYes = () => {
    setShowVibes(true);
    // Pre-select the last pick if the user hasn't chosen one yet
    if (!value || value === "skip") {
      onChange(lastPick || "soft_lifted");
    }
  };

  const handleSkip = () => {
    setShowVibes(false);
    onChange("skip");
  };

  // Initial Yes/Not right now state — value is null
  if (value === null && !showVibes) {
    return (
      <div
        className="rounded-2xl bg-white border border-[#EADDD7] p-5 soft-shadow"
        data-testid="contour-prompt"
      >
        <div className="flex items-start gap-3">
          <span className="inline-flex h-8 w-8 rounded-full bg-[#F5DCD7] items-center justify-center shrink-0 mt-0.5">
            <Sparkles className="h-4 w-4 text-[#9B6A6C]" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[#2C2123] font-medium text-sm sm:text-base leading-relaxed">
              Would you like to explore contouring in this routine today?{" "}
              <span className="text-[#9B6A6C]">✨</span>
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                onClick={handleYes}
                className="rounded-full bg-[#9B6A6C] hover:bg-[#7d5455] text-white px-5 py-2 text-sm font-medium transition-all"
                data-testid="contour-prompt-yes"
              >
                Yes
              </button>
              <button
                type="button"
                onClick={handleSkip}
                className="rounded-full bg-transparent hover:bg-[#FAF5F1] text-[#5C4B51] border border-[#EADDD7] px-5 py-2 text-sm font-medium transition-all"
                data-testid="contour-prompt-no"
              >
                Not right now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // "Skip" picked — show a small confirmation chip with an undo
  if (value === "skip") {
    return (
      <div
        className="rounded-2xl bg-[#FAF5F1] border border-[#EADDD7] p-4"
        data-testid="contour-skipped"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="text-[#5C4B51] text-sm">
            <span className="text-[#9B6A6C]">✓</span>{" "}
            No contour this routine — we'll skip it.
          </div>
          <button
            type="button"
            onClick={() => {
              setShowVibes(true);
              onChange(lastPick || "soft_lifted");
            }}
            className="text-[#9B6A6C] hover:text-[#7d5455] text-xs font-medium underline underline-offset-2 transition-colors"
            data-testid="contour-skipped-undo"
          >
            Actually, let's contour
          </button>
        </div>
      </div>
    );
  }

  // Vibe-picker state
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-2xl bg-white border border-[#EADDD7] p-5 soft-shadow"
      data-testid="contour-vibes"
    >
      <div className="flex items-start gap-3 mb-4">
        <span className="inline-flex h-8 w-8 rounded-full bg-[#F5DCD7] items-center justify-center shrink-0 mt-0.5">
          <Sparkles className="h-4 w-4 text-[#9B6A6C]" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[#2C2123] font-medium text-sm sm:text-base">
            What kind of contour vibe are we creating today?{" "}
            <span className="text-[#9B6A6C]">💄</span>
          </p>
        </div>
        <button
          type="button"
          onClick={handleSkip}
          className="text-[#8A7D82] hover:text-[#5C4B51] text-xs font-medium transition-colors shrink-0"
          data-testid="contour-vibes-skip"
        >
          Skip contour
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {VIBES.map((vibe) => {
          const selected = value === vibe.value;
          return (
            <button
              key={vibe.value}
              type="button"
              onClick={() => onChange(vibe.value)}
              className={`text-left rounded-xl border px-4 py-3 transition-all ${
                selected
                  ? "bg-gradient-to-br from-[#F5DCD7] to-[#EAC9C2] border-[#9B6A6C] shadow-sm"
                  : "bg-[#FAF5F1] border-[#EADDD7] hover:border-[#D5A8A4] hover:bg-[#F5DCD7]/40"
              }`}
              data-testid={`contour-vibe-${vibe.value}`}
              aria-pressed={selected}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium text-[#2C2123] text-sm leading-tight">
                  {vibe.label}
                </div>
                <AnimatePresence>
                  {selected && (
                    <motion.span
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      className="inline-flex h-5 w-5 rounded-full bg-[#9B6A6C] items-center justify-center shrink-0"
                    >
                      <Check className="h-3 w-3 text-white" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
              <div className="text-[#5C4B51] text-xs mt-1 leading-snug">
                {vibe.description}
              </div>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
};

export default ContourVibePicker;
